import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { supplierModel } from '../../../models/supplierModel';
import supplierHelpdeskService from '../../../services/supplierHelpdeskService';
import { toast } from 'react-toastify';
import { Paperclip, Eye, Download, FileText } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import SupplierBillingSection from './components/SupplierBillingSection';

const SupplierTicketDetail = () => {
    const { id } = useParams();
    const [ticket, setTicket] = useState(null);
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    
    const { supplier } = useSupplierAuth();

    useEffect(() => {
        if (supplier && id) {
            loadTicket();
        }
    }, [supplier, id]);

    const loadTicket = async () => {
        try {
            setLoading(true);
            const response = await supplierModel.getHelpdeskTicket(id);
            
            if (response.status === 1) {
                setTicket(response.data);
                setUpdates(response.data.updates || []);
            } else {
                toast.error('Failed to load ticket details');
            }
        } catch (error) {
            console.error('Error loading ticket:', error);
            toast.error('Error loading ticket: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBillingUpdate = () => {
        // Reload ticket to get updated billing information
        loadTicket();
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        
        if (!comment.trim()) {
            toast.error('Please enter a comment');
            return;
        }

        setAddingComment(true);
        try {
            const response = await supplierModel.addHelpdeskTicketComment(id, comment.trim());
            
            if (response.status === 1) {
                toast.success('Comment added successfully');
                setComment('');
                await loadTicket(); // Reload to show new comment
            } else {
                toast.error(response.message || 'Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error('Error adding comment: ' + error.message);
        } finally {
            setAddingComment(false);
        }
    };

    const getStatusBadge = (status) => {
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${supplierHelpdeskService.getStatusBadgeClass(status)}`}>
                {supplierHelpdeskService.getStatusConfig()[status]?.label || status}
            </span>
        );
    };

    const getPriorityBadge = (priority) => {
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${supplierHelpdeskService.getPriorityBadgeClass(priority)}`}>
                {priority?.toUpperCase()}
            </span>
        );
    };

    const formatUpdateTime = (timestamp) => {
        // Handle both Unix timestamp and date string
        const date = isNaN(timestamp) ? new Date(timestamp) : new Date(timestamp * 1000);
        return date.toLocaleString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper function to get field value, checking custom_fields if main field is empty
    const getFieldValue = (fieldName, fallbackValue = 'N/A') => {
        if (ticket[fieldName]) {
            return ticket[fieldName];
        }
        
        // Check custom_fields if main field is empty
        if (ticket.custom_fields) {
            try {
                const customFields = typeof ticket.custom_fields === 'string' 
                    ? JSON.parse(ticket.custom_fields) 
                    : ticket.custom_fields;
                    
                if (customFields && customFields[fieldName]) {
                    return customFields[fieldName];
                }
            } catch (error) {
                // If JSON parsing fails, custom_fields is not valid JSON
                // This can happen if it's an empty string, "0", or other non-JSON value
                console.warn('Failed to parse custom_fields as JSON:', ticket.custom_fields);
                // Return fallback value instead of crashing
            }
        }
        
        return fallbackValue;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading ticket...</span>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="p-6 max-w-4xl mx-auto">
                <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">Ticket not found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        The ticket you're looking for doesn't exist or you don't have access to it.
                    </p>
                    <div className="mt-6">
                        <Link
                            to="/supplier/helpdesk"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            Back to Tickets
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center mb-4">
                    <Link
                        to="/supplier/helpdesk"
                        className="mr-4 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </Link>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900">
                            Ticket #{ticket.ticket_number}
                        </h1>
                        <p className="text-gray-600">
                            Created on {supplierHelpdeskService.formatDate(ticket.created_at)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Ticket Details Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="mb-4">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                {ticket.title}
                            </h2>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>Category: {ticket.ticket_type?.replace('_', ' ').toUpperCase()}</span>
                                <span>•</span>
                                <span>Requester: {getFieldValue('requester_name')}</span>
                            </div>
                        </div>
                        
                        <div className="prose prose-sm max-w-none">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Description:</h3>
                            <div className="bg-gray-50 rounded-lg p-4">
                                {ticket.description && ticket.description.trim() ? (
                                    ticket.description.includes('<') ? (
                                        <div 
                                            dangerouslySetInnerHTML={{ 
                                                __html: ticket.description
                                                    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                                    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                                    .replace(/<link[^>]*>/gi, '')
                                                    .replace(/<form[^>]*>[\s\S]*?<\/form>/gi, '')
                                                    .replace(/<img([^>]*)>/gi, '<img$1 class="img-fluid rounded max-w-full h-auto" />')
                                            }}
                                        />
                                    ) : (
                                        <div className="whitespace-pre-wrap">{ticket.description}</div>
                                    )
                                ) : (
                                    <div className="text-gray-500 italic">No description provided.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Attachments - Only show if there are attachments */}
                    {ticket.attachments && ticket.attachments.length > 0 && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <Paperclip className="w-5 h-5 mr-2 text-blue-600" />
                                Attachments ({ticket.attachments.length})
                            </h2>
                            
                            {(() => {
                                const images = ticket.attachments.filter(att => 
                                    att.mime_type?.startsWith('image/') || att.file_type?.startsWith('image/') ||
                                    /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(att.file_name || '')
                                );
                                const files = ticket.attachments.filter(att => 
                                    !(att.mime_type?.startsWith('image/') || att.file_type?.startsWith('image/') ||
                                    /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(att.file_name || ''))
                                );

                                return (
                                    <>
                                        {/* Images Section */}
                                        {images.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                    <Eye className="w-4 h-4 mr-2" />
                                                    Images ({images.length})
                                                </h3>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                    {images.map((image, index) => {
                                                        const fileUrl = `${API_BASE_URL}/attachment/supplier/${image.file_path}`;
                                                        return (
                                                            <div key={index} className="group relative bg-gray-50 rounded-lg border overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.open(fileUrl, '_blank')}>
                                                                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                                                    <img 
                                                                        src={fileUrl} 
                                                                        alt={image.file_name || `Image ${index + 1}`}
                                                                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full"><Eye class="w-8 h-8 text-gray-400" /></div>';
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="p-2 bg-white border-t">
                                                                    <p className="text-xs font-medium text-gray-900 truncate" title={image.file_name}>
                                                                        {image.file_name || `Image ${index + 1}`}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {image.file_size ? `${Math.round(image.file_size / 1024)} KB` : 'Unknown size'}
                                                                    </p>
                                                                </div>
                                                                {/* Overlay with actions */}
                                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                                                                    <div className="flex space-x-2 pointer-events-auto">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                window.open(fileUrl, '_blank');
                                                                            }}
                                                                            className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                                                            title="View full size"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const link = document.createElement('a');
                                                                                link.href = fileUrl + '?download=1';
                                                                                link.download = image.file_name || `image-${index + 1}`;
                                                                                link.click();
                                                                            }}
                                                                            className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                                                            title="Download"
                                                                        >
                                                                            <Download className="w-4 h-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Files Section */}
                                        {files.length > 0 && (
                                            <div>
                                                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                    <FileText className="w-4 h-4 mr-2" />
                                                    Files ({files.length})
                                                </h3>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {files.map((file, index) => {
                                                        // Determine file icon based on extension
                                                        const getFileIcon = (fileName) => {
                                                            const ext = fileName?.split('.').pop()?.toLowerCase();
                                                            switch (ext) {
                                                                case 'pdf': return '📄';
                                                                case 'doc': case 'docx': return '📝';
                                                                case 'xls': case 'xlsx': return '📊';
                                                                case 'ppt': case 'pptx': return '📊';
                                                                case 'zip': case 'rar': case '7z': return '🗜️';
                                                                case 'txt': return '📄';
                                                                default: return '📎';
                                                            }
                                                        };

                                                        const fileUrl = `${API_BASE_URL}/attachment/supplier/${file.file_path}`;

                                                        return (
                                                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors group">
                                                                <div className="text-2xl flex-shrink-0">
                                                                    {getFileIcon(file.file_name)}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate" title={file.file_name}>
                                                                        {file.file_name || `File ${index + 1}`}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {file.file_size ? `${Math.round(file.file_size / 1024)} KB` : 'Unknown size'}
                                                                        {(file.file_type || file.mime_type) && ` • ${file.file_type || file.mime_type}`}
                                                                    </p>
                                                                </div>
                                                                <div className="flex space-x-2">
                                                                    <button
                                                                        onClick={() => window.open(fileUrl, '_blank')}
                                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                        title="View"
                                                                    >
                                                                        <Eye className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            const link = document.createElement('a');
                                                                            link.href = fileUrl + '?download=1';
                                                                            link.download = file.file_name || `file-${index + 1}`;
                                                                            link.click();
                                                                        }}
                                                                        className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                        title="Download"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Billing Section - Only show if billing is enabled */}
                    {ticket.billing_enabled == 1 && (
                        <SupplierBillingSection 
                            ticket={ticket} 
                            onBillingUpdate={handleBillingUpdate}
                        />
                    )}

                    {/* Updates/Comments */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Activity & Comments
                        </h3>
                        
                        {updates.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <p>No comments yet. Be the first to add one!</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {updates.map((update, index) => (
                                    <div key={index} className="border-l-4 border-blue-200 pl-4 pb-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <span className="font-medium text-gray-900">
                                                        {update.user_full_name || update.supplier_user_name || 'System'}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        {update.ticket_update_title || 'Comment'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-500 mb-2">
                                                    {formatUpdateTime(update.ticket_update_time)}
                                                </div>
                                                {update.ticket_update && (
                                                    <div 
                                                        className="bg-gray-50 rounded p-3 text-sm" 
                                                        dangerouslySetInnerHTML={{ __html: update.ticket_update }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add Comment Form */}
                        <div className="mt-6 border-t pt-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Add a Comment</h4>
                            <form onSubmit={handleAddComment} className="space-y-4">
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows="4"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    placeholder="Add additional information, updates, or questions about this ticket..."
                                    maxLength="1000"
                                />
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500">
                                        {comment.length}/1000 characters
                                    </span>
                                    <button
                                        type="submit"
                                        disabled={addingComment || !comment.trim()}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                    >
                                        {addingComment ? 'Adding...' : 'Add Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Status Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ticket Information</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-gray-500">Status</label>
                                <div className="mt-1">
                                    {getStatusBadge(ticket.status)}
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Priority</label>
                                <div className="mt-1">
                                    {getPriorityBadge(ticket.priority)}
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Ticket ID</label>
                                <div className="mt-1 text-sm text-gray-900">
                                    {ticket.ticket_id}
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Created</label>
                                <div className="mt-1 text-sm text-gray-900">
                                    {supplierHelpdeskService.formatDate(ticket.created_at)}
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Category</label>
                                <div className="mt-1 text-sm text-gray-900">
                                    {ticket.ticket_type?.replace('_', ' ').toUpperCase()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions Card */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
                        
                        <div className="space-y-3">
                            <button
                                onClick={loadTicket}
                                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Refresh
                            </button>
                            
                            <Link
                                to="/supplier/helpdesk/new"
                                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Create New Ticket
                            </Link>
                        </div>
                    </div>

                    {/* Help Card */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 className="text-sm font-medium text-blue-800">Need Help?</h4>
                                <p className="mt-1 text-sm text-blue-700">
                                    Our support team will respond to your ticket as soon as possible. You'll receive email notifications for any updates.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupplierTicketDetail;